import React, { useState } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Link } from '@vk-hr-tek/ui/Link';

import { modules } from '@app/modules';

import { Notification } from '../../types';

import { NotificationItemHeader } from './NotificationItemHeader';
import { NotificationItemBody } from './NotificationItemBody';
import { useStyles } from './NotificationItem.styles';

export interface NotificationItemProps {
  data: Notification;
  variant?: 'list' | 'widget';
  onReadClick?: (id: string) => void;
  onClose?: () => void;
}

export const NotificationItem = ({
  data: {
    id,
    icon,
    notifiableName,
    actionName,
    message,
    isRead,
    createdAt,
    payload,
  },
  variant = 'list',
  onReadClick,
  onClose,
}: NotificationItemProps) => {
  const { classes } = useStyles();

  const [isMarkReadTooltipOpen, setIsMarkReadTooltipOpen] = useState(false);

  const isWidgetVariant = variant === 'widget';

  const isClickable = !!payload?.news_id;

  const handleReadClick = (
    event?: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    onReadClick?.(id);
  };

  const handleMarkReadTooltipOpen = () => {
    setIsMarkReadTooltipOpen(true);
  };

  const handleMarkReadTooltipClose = () => {
    setIsMarkReadTooltipOpen(false);
  };

  const handleLinkClick = () => {
    handleReadClick();

    if (onClose) {
      onClose();
    }
  };

  const iconWrapperSize = isWidgetVariant ? '40px' : '48px';

  const Icon = icon;

  const content = (
    <Box
      display="flex"
      gap="12"
      p="8"
      radius="m"
      className={classes.wrapper}
      cursor={isClickable ? 'pointer' : 'default'}
    >
      <Box
        width={iconWrapperSize}
        height={iconWrapperSize}
        radius="round"
        bgcolor="accent.bg.blue"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexShrink="0"
      >
        {Icon && (
          <Icon color="blue" size={isWidgetVariant ? 'small' : 'normal'} />
        )}
      </Box>

      <Tooltip
        title={
          <Box display="flex" flexDirection="column" gap="4">
            {message}
            <Typography variant="caption" color="text.light.tertirary">
              {notifiableName} • {actionName}
            </Typography>
          </Box>
        }
        placement="bottom"
        disabled={!isWidgetVariant || isMarkReadTooltipOpen}
      >
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          {...(!isWidgetVariant && { gap: '2' })}
        >
          <NotificationItemHeader
            variant={variant}
            name={notifiableName}
            action={actionName}
            createdAt={createdAt}
          />

          <NotificationItemBody
            variant={variant}
            message={message}
            read={isRead}
            onRead={handleReadClick}
            onTooltipOpen={handleMarkReadTooltipOpen}
            onTooltipClose={handleMarkReadTooltipClose}
          />
        </Box>
      </Tooltip>
    </Box>
  );

  if (isClickable) {
    const newsId = payload.news_id;
    const commentId = payload.comment_id;

    if (newsId) {
      const url = `/${modules.news.basename}/${newsId}${
        commentId ? `#${commentId}` : ''
      }`;

      return (
        <Box onClick={handleLinkClick}>
          <Link to={url} variant="simple" stroke={false}>
            {content}
          </Link>
        </Box>
      );
    }
  }

  return content;
};
